import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { DateInvite } from '@/api/types';
import { endpoints } from '@/api';
import { qk, useConfig, useDateInvites } from '@/api/queries';
import { toast } from '@/stores/toasts';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Sheet, Modal } from '@/components/ui/Modal';
import { ListSkeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/States';
import { cn } from '@/lib/cn';
import { useInviteComposer } from './composer';
import { dateInviteError } from './errors';
import { SlotBuilder } from './SlotBuilder';
import { PlannerSheet, needsTimes } from './PlannerDeck';
import { confirmable, quickChips, slotInputs, slotLabel } from './slots';
import { STATUS_LABEL, buildIcs, copyText, downloadIcs, isLive, isLobbyable, linkFor, lobbyUrl, otherPerson, rememberLink, whenLabel } from './share';
import { MyDatesLink } from '@/features/after/MyDatesLink';
import { MyLinkButton } from '@/features/links/LinkRequests';
import { SeriesSection } from '@/features/series/SeriesSection';
import { useSeriesComposer } from '@/features/series/SeriesSheet';

/** Your move (host: pick a time; guest: plan) / upcoming (a time is set) / waiting / past. */
export function groupInvites(list: DateInvite[]): { action: DateInvite[]; open: DateInvite[]; upcoming: DateInvite[]; past: DateInvite[] } {
  const isAction = (i: DateInvite) => (i.role === 'host' && i.status === 'awaiting_host') || needsTimes(i);
  const action = list.filter(isAction);
  const upcoming = list
    .filter((i) => isLobbyable(i))
    .sort((a, b) => Date.parse(a.starts_at ?? a.created_at) - Date.parse(b.starts_at ?? b.created_at));
  const open = list.filter((i) => isLive(i) && !isLobbyable(i) && !isAction(i));
  const past = list.filter((i) => !isLive(i));
  return { action, open, upcoming, past };
}

/** Reschedule (host): new times — the guest picks again — or now (SPEC_V6_1 §4, SPEC_V6_2 §1). */
export function RescheduleSheet({ invite, onClose, onDone }: { invite: DateInvite | null; onClose: () => void; onDone?: (v: DateInvite) => void }) {
  const qc = useQueryClient();
  const [when, setWhen] = useState<'now' | 'times'>('times');
  const [times, setTimes] = useState<Date[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!invite) return;
    const q = quickChips();
    setTimes(q.length ? [q[0].at] : []);
    setWhen('times');
    setErr(null);
  }, [invite]);
  const save = async () => {
    if (!invite || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const v = await endpoints.rescheduleDateInvite(invite.id, when === 'now' ? { now: true } : { slots: slotInputs(times, invite.length_min) });
      qc.setQueryData<{ invites: DateInvite[] }>(qk.dateInvites, (p) => (p ? { invites: p.invites.map((x) => (x.id === v.id ? v : x)) } : p));
      onDone?.(v);
      toast.success(when === 'now' ? 'Meet whenever you’re both ready' : 'New times sent', 'Same link — we’ve let them know.');
      onClose();
    } catch (e) {
      setErr(dateInviteError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={!!invite} onClose={onClose} title="Pick new times" description="Same link, fresh times. They’ll pick what works." testId="reschedule-sheet">
      <div className="space-y-4">
        <div role="radiogroup" aria-label="When" className="grid grid-cols-2 gap-1 rounded-full bg-raised p-1">
          {(['now', 'times'] as const).map((w) => (
            <button
              key={w}
              type="button"
              role="radio"
              aria-checked={when === w}
              onClick={() => setWhen(w)}
              className={cn('h-10 rounded-full text-[15px] font-semibold transition-colors duration-150', when === w ? 'bg-primary text-ground' : 'text-muted hover:text-fg')}
              data-testid={`reschedule-${w}`}
            >
              {w === 'now' ? 'Now' : 'Offer times'}
            </button>
          ))}
        </div>
        {when === 'times' && <SlotBuilder value={times} onChange={setTimes} testId="reschedule-slots" />}
        {err && (
          <p role="alert" className="text-[15px] font-medium text-danger">
            {err}
          </p>
        )}
        <Button block size="lg" icon="calendar" onClick={() => save()} loading={busy} disabled={when === 'times' && times.length === 0} data-testid="reschedule-save">
          {when === 'now' ? 'Meet now instead' : 'Send new times'}
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * Host, awaiting_host: "Maya planned your date — pick a time" with the
 * overlaps highlighted; one tap confirms (SPEC_V6_2 §5).
 */
export function ConfirmTimes({ inv, onDone, compact }: { inv: DateInvite; onDone?: (v: DateInvite) => void; compact?: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const options = confirmable(inv);
  const confirm = async (slotId: string) => {
    if (busy) return;
    setBusy(slotId);
    try {
      const v = await endpoints.confirmDateInvite(inv.id, slotId);
      qc.setQueryData(qk.dateInvite(v.id), v);
      qc.setQueryData<{ invites: DateInvite[] }>(qk.dateInvites, (p) => (p ? { invites: p.invites.map((x) => (x.id === v.id ? v : x)) } : p));
      onDone?.(v);
      toast.success('It’s a date!', 'We’ve sent you both a calendar invite.');
    } catch (e) {
      toast.error('Couldn’t confirm', dateInviteError(e));
    } finally {
      setBusy(null);
    }
  };
  if (options.length === 0) return <p className="text-[15px] text-muted">None of the times work any more. Offer new ones.</p>;
  return (
    <ul className={cn('grid gap-2', compact ? 'grid-cols-1' : 'sm:grid-cols-2')} data-testid="confirm-times">
      {options.map(({ slot, kind }) => {
        const l = slotLabel(slot.starts_at);
        return (
          <li key={slot.id}>
            <button
              type="button"
              onClick={() => void confirm(slot.id)}
              disabled={!!busy}
              aria-busy={busy === slot.id || undefined}
              className={cn(
                'flex w-full items-center gap-3 rounded-card bg-raised p-3.5 pl-4 text-left transition-colors hover:bg-line focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg disabled:opacity-60',
              )}
              data-testid="confirm-slot"
              data-kind={kind}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-muted">{l.day}</span>
                <span className="block text-[22px] font-semibold leading-tight tabular-nums">{l.time}</span>
                <span className={cn('flex items-center gap-1 text-[13px] font-medium', kind === 'works' ? 'text-go' : 'text-muted')}>
                  {kind === 'works' && <Icon name="check" size={14} />}
                  {kind === 'works' ? 'Works for both of you' : 'Their suggestion'}
                </span>
              </span>
              <span className={cn('inline-flex h-10 items-center rounded-full px-4 text-[15px] font-semibold', kind === 'works' ? 'bg-green text-night' : 'bg-primary text-ground')}>
                {busy === slot.id ? '…' : 'Confirm'}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

type ConfirmKind = 'cancel' | 'decline' | 'newlink';

function InviteRow({ inv, onReschedule, onConfirm, onPlan }: { inv: DateInvite; onReschedule: (i: DateInvite) => void; onConfirm: (c: { invite: DateInvite; kind: ConfirmKind }) => void; onPlan: (i: DateInvite) => void }) {
  const share = useInviteComposer((s) => s.share);
  const other = otherPerson(inv);
  const live = isLive(inv);
  const url = inv.status === 'open' && inv.role === 'host' ? linkFor(inv.id) : null;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!url) return;
    const ok = await copyText(url);
    setCopied(ok);
    if (ok) toast.success('Link copied');
    else share({ ...inv, url });
  };
  const awaitingMe = inv.role === 'host' && inv.status === 'awaiting_host';
  const planMe = needsTimes(inv);
  const slots = inv.slots ?? [];
  const whenText = isLobbyable(inv) || slots.length === 0 ? whenLabel(inv.starts_at) : `${slots.length} ${slots.length === 1 ? 'time' : 'times'} offered`;
  const title = awaitingMe
    ? `${other?.display_name ?? 'They'} planned your date — pick a time`
    : planMe
      ? `${other?.display_name ?? 'They'} invited you — plan your date`
      : other
        ? `Date with ${other.display_name}`
        : 'Open invite';
  return (
    <li
      className="p-4"
      data-testid="date-invite-row"
      data-status={inv.status}
      data-role={inv.role}
    >
      <div className="flex items-center gap-3">
        {other ? (
          <Avatar avatar={other.avatar} size={44} label={other.display_name} />
        ) : (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-raised text-[17px] font-semibold text-muted" aria-hidden>
            ?
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-snug" data-testid="date-invite-title">
            {title}
          </p>
          <p className="truncate text-[13px] tabular-nums text-muted">
            {whenText} · {inv.length_min} min{inv.role === 'guest' && !planMe ? ' · they invited you' : ''}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2.5 py-1 text-[13px] font-medium',
            inv.missed ? 'bg-yellow text-night' : isLobbyable(inv) || awaitingMe || planMe ? 'bg-primary text-ground' : live ? 'bg-raised text-fg' : 'bg-raised text-muted',
          )}
          data-testid="date-invite-status"
        >
          {inv.missed ? 'Missed' : planMe ? 'Your move' : inv.role === 'guest' && inv.status === 'awaiting_host' ? 'Waiting on them' : STATUS_LABEL[inv.status]}
        </span>
      </div>
      {inv.note && <p className="mt-2 truncate text-[15px] text-muted">“{inv.note}”</p>}
      {/* v9 §A5: after a date, make it a weekly thing. */}
      {inv.status === 'completed' && other && <MakeWeekly inv={inv} />}
      {awaitingMe && (
        <div className="mt-3">
          <ConfirmTimes inv={inv} />
        </div>
      )}
      {live && (
        <div className="mt-3 flex flex-wrap gap-2">
          {planMe && (
            <Button size="sm" icon="sparkle" onClick={() => onPlan(inv)} data-testid="dates-plan">
              Plan our date
            </Button>
          )}
          {isLobbyable(inv) && (
            <Link to={`/dates/${inv.id}`} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-ground" data-testid="dates-open-lobby">
              <Icon name="cam" size={16} /> {inv.session_id ? 'Join date' : 'Lobby'}
            </Link>
          )}
          {!isLobbyable(inv) && inv.status !== 'open' && (
            <Link to={`/dates/${inv.id}`} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-raised px-4 text-sm font-semibold transition-colors hover:bg-line" data-testid="dates-open-plan">
              <Icon name="calendar" size={16} /> Details
            </Link>
          )}
          {inv.status === 'open' && inv.role === 'host' && (
            <>
              <Link to={`/dates/${inv.id}`} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-raised px-4 text-sm font-semibold transition-colors hover:bg-line" data-testid="dates-open-lobby">
                Lobby
              </Link>
              {url ? (
                <Button size="sm" variant="subtle" icon={copied ? 'check' : 'copy'} onClick={() => void copy()} data-testid="dates-copy-link">
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              ) : (
                <Button size="sm" variant="subtle" icon="link" onClick={() => onConfirm({ invite: inv, kind: 'newlink' })} data-testid="dates-new-link">
                  New link
                </Button>
              )}
            </>
          )}
          {inv.role === 'host' && (
            <Button size="sm" variant="ghost" icon="calendar" onClick={() => onReschedule(inv)} data-testid="dates-reschedule">
              {inv.missed ? 'Reschedule' : awaitingMe ? 'Offer other times' : 'Change time'}
            </Button>
          )}
          {isLobbyable(inv) && inv.starts_at && (
            <Button size="sm" variant="ghost" icon="download" onClick={() => downloadIcs(buildIcs(inv, other?.display_name ?? null, lobbyUrl(inv.id)))} data-testid="dates-ics">
              Calendar
            </Button>
          )}
          {inv.role === 'host' ? (
            <Button size="sm" variant="ghost" icon="x" onClick={() => onConfirm({ invite: inv, kind: 'cancel' })} data-testid="dates-cancel">
              Cancel
            </Button>
          ) : (
            <Button size="sm" variant="ghost" icon="x" onClick={() => onConfirm({ invite: inv, kind: 'decline' })} data-testid="dates-decline">
              Can’t make it
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/** "Make it a weekly thing" on a past date (v9 §A5), prefilled with that date's day and time. */
function MakeWeekly({ inv }: { inv: DateInvite }) {
  const start = useSeriesComposer((s) => s.start);
  const config = useConfig();
  const other = otherPerson(inv);
  if (!other || config.data?.features.weekly_dates === false) return null;
  return (
    <Button
      size="sm"
      variant="subtle"
      accent="pop"
      icon="heart"
      className="mt-3"
      onClick={() => start({ id: other.id, display_name: other.display_name, avatar: other.avatar }, new Date(inv.starts_at ?? inv.created_at))}
      data-testid="dates-make-weekly"
    >
      Make it a weekly thing
    </Button>
  );
}

/** Friends → Dates (SPEC_V6_1 §8, SPEC_V6_2 §5): your move / upcoming / waiting / past. */
export function DatesSection() {
  const config = useConfig();
  const enabled = config.data?.features.date_invites !== false;
  const q = useDateInvites(enabled);
  const qc = useQueryClient();
  const start = useInviteComposer((s) => s.start);
  const share = useInviteComposer((s) => s.share);
  const [resched, setResched] = useState<DateInvite | null>(null);
  const [plan, setPlan] = useState<DateInvite | null>(null);
  const [confirm, setConfirm] = useState<{ invite: DateInvite; kind: ConfirmKind } | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPast, setShowPast] = useState(false);
  if (!enabled) return null;

  const run = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    try {
      const { invite, kind } = confirm;
      const v =
        kind === 'cancel' ? await endpoints.cancelDateInvite(invite.id) : kind === 'decline' ? await endpoints.declineDateInvite(invite.id) : await endpoints.newDateInviteLink(invite.id);
      qc.setQueryData<{ invites: DateInvite[] }>(qk.dateInvites, (p) => (p ? { invites: p.invites.map((x) => (x.id === v.id ? v : x)) } : p));
      if (kind === 'newlink') {
        rememberLink(v.id, v.url);
        share(v);
      } else toast.info(kind === 'cancel' ? 'Invite cancelled' : 'We’ve let them know', kind === 'cancel' ? 'The link no longer works.' : 'No reason needed.');
      setConfirm(null);
    } catch (e) {
      toast.error('That didn’t go through', dateInviteError(e));
    } finally {
      setBusy(false);
    }
  };

  const { action, open, upcoming, past } = groupInvites(q.data?.invites ?? []);
  const rowProps = { onReschedule: setResched, onConfirm: setConfirm, onPlan: setPlan };
  const group = (label: string, list: DateInvite[], testId: string) =>
    list.length > 0 && (
      <div>
        <p className="mb-2 text-[13px] font-medium text-muted">{label}</p>
        <ul className="divide-y divide-line overflow-hidden rounded-card bg-surface" data-testid={testId}>
          {list.map((i) => (
            <InviteRow key={i.id} inv={i} {...rowProps} />
          ))}
        </ul>
      </div>
    );
  return (
    <section id="dates" className="mb-8 scroll-mt-20" aria-labelledby="dates-title" data-testid="friends-dates">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-baseline gap-2 text-[17px] font-semibold leading-snug">
          <span id="dates-title">Dates</span>
          {upcoming.length + action.length > 0 && <span className="text-[15px] font-medium tabular-nums text-muted">{upcoming.length + action.length}</span>}
        </h2>
        <div className="flex items-center gap-2">
          <MyDatesLink />
          <MyLinkButton />
          <Button size="sm" variant="subtle" accent="pop" icon="plus" onClick={() => start({ source: 'friends' })} data-testid="dates-new-invite">
            Invite someone
          </Button>
        </div>
      </div>
      {/* v9 area A: weekly date nights. Requests on your link live in /requests (v12). */}
      <div className="mb-4 space-y-4 empty:hidden">
        <SeriesSection />
      </div>
      {q.isLoading ? (
        <ListSkeleton rows={2} label="Loading your dates" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} compact />
      ) : action.length + open.length + upcoming.length + past.length === 0 ? (
        <p className="rounded-card bg-surface p-5 text-[15px] text-muted" data-testid="dates-empty">
          Met someone elsewhere? Send a date link. No app needed.
        </p>
      ) : (
        <div className="space-y-4">
          {group('Your move', action, 'dates-action')}
          {group('Upcoming', upcoming, 'dates-upcoming')}
          {group('Waiting', open, 'dates-open')}
          {past.length > 0 && (
            <div>
              <button type="button" className="mb-2 inline-flex h-9 items-center gap-1 text-[13px] font-medium tabular-nums text-muted transition-colors hover:text-fg" onClick={() => setShowPast((s) => !s)} aria-expanded={showPast} data-testid="dates-past-toggle">
                Past ({past.length}) <Icon name="chevron-down" size={14} className={cn('transition-transform', showPast && 'rotate-180')} />
              </button>
              {showPast && (
                <ul className="divide-y divide-line overflow-hidden rounded-card bg-surface" data-testid="dates-past">
                  {past.slice(0, 10).map((i) => (
                    <InviteRow key={i.id} inv={i} {...rowProps} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
      <RescheduleSheet invite={resched} onClose={() => setResched(null)} />
      <PlannerSheet invite={plan} onClose={() => setPlan(null)} />
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.kind === 'cancel' ? 'Cancel this invite?' : confirm?.kind === 'decline' ? 'Can’t make it?' : 'Make a new link?'}
        description={
          confirm?.kind === 'cancel'
            ? 'The link stops working and they’ll see it was cancelled.'
            : confirm?.kind === 'decline'
              ? 'We’ll let them know — no reason needed. You stay friends.'
              : 'The link lives on the device you made it on. A new link replaces it — the old one stops working.'
        }
        testId="dates-confirm"
      >
        <div className="grid grid-cols-2 gap-2">
          <Button variant="subtle" onClick={() => setConfirm(null)} disabled={busy}>
            Keep it
          </Button>
          <Button accent={confirm?.kind === 'newlink' ? 'pop' : 'danger'} onClick={() => run()} loading={busy} data-testid="dates-confirm-yes">
            {confirm?.kind === 'cancel' ? 'Cancel invite' : confirm?.kind === 'decline' ? 'Let them know' : 'New link'}
          </Button>
        </div>
      </Modal>
    </section>
  );
}
